<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <card :title="$t('timeRegistrationClockInLocations.title')" :on-add-object="openCreate">
          <div v-if="!loaded" class="text-center text-secondary py-4">{{ $t('common.loading') }}</div>
          <div v-else-if="locations.length === 0" class="text-center text-secondary py-4">
            {{ $t('timeRegistrationClockInLocations.noLocations') }}
          </div>
          <el-table v-else :data="locations" row-key="id" style="width:100%">
            <el-table-column :label="$t('timeRegistrationClockInLocations.name')" prop="name" min-width="140" />
            <el-table-column :label="$t('locations.latitude')" prop="latitude" width="140" />
            <el-table-column :label="$t('locations.longitude')" prop="longitude" width="140" />
            <el-table-column :label="$t('timeRegistrationClockInLocations.radiusMeters')" prop="radiusMeters" width="140" />
            <el-table-column :label="$t('common.status')" width="110">
              <template #default="scope">
                <el-tag v-if="scope.row.isArchived" type="info" size="small">{{ $t('timeRegistrationGroups.archived') }}</el-tag>
                <el-tag v-else type="success" size="small">{{ $t('timeRegistrationGroups.active') }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="140" align="right">
              <template #default="scope">
                <el-button size="small" @click="openEdit(scope.row)">
                  <i class="material-icons" style="font-size:1rem">edit</i>
                </el-button>
                <el-button size="small" type="danger" @click="confirmDelete(scope.row)">
                  <i class="material-icons" style="font-size:1rem">delete</i>
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="mt-3">
            <el-checkbox v-model="includeArchived" @change="fetchLocations">{{ $t('timeRegistrationGroups.includeArchived') }}</el-checkbox>
          </div>
        </card>
      </div>
    </div>

    <el-dialog v-model="showForm" :title="editMode ? $t('timeRegistrationClockInLocations.editLocation') : $t('timeRegistrationClockInLocations.newLocation')" width="520">
      <div class="mb-3">
        <label class="form-label">{{ $t('timeRegistrationClockInLocations.name') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.name" maxlength="100" />
      </div>
      <div class="row">
        <div class="col-md-6 mb-3">
          <label class="form-label">{{ $t('locations.latitude') }} <span class="text-danger">*</span></label>
          <el-input v-model="form.latitude" type="number" step="0.000001" />
        </div>
        <div class="col-md-6 mb-3">
          <label class="form-label">{{ $t('locations.longitude') }} <span class="text-danger">*</span></label>
          <el-input v-model="form.longitude" type="number" step="0.000001" />
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label">{{ $t('timeRegistrationClockInLocations.radiusMeters') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.radiusMeters" type="number" step="1" min="1" />
        <small class="text-secondary">{{ $t('timeRegistrationClockInLocations.radiusHint') }}</small>
      </div>
      <div v-if="navigatorSupportsGeolocation" class="mb-3">
        <el-button size="small" :loading="fetchingLocation" @click="useCurrentLocation">
          <i class="material-icons me-1" style="font-size:1rem;vertical-align:middle">my_location</i>
          {{ $t('timeRegistrationClockInLocations.useCurrentLocation') }}
        </el-button>
      </div>
      <div v-if="editMode" class="mb-2">
        <el-checkbox v-model="form.isArchived">{{ $t('timeRegistrationGroups.archived') }}</el-checkbox>
      </div>
      <template #footer>
        <el-button @click="showForm = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">{{ $t('common.submit') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../services/api';
import Card from '../../components/Card.vue';
import { ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElTag, ElCheckbox, ElMessageBox } from 'element-plus';

export default {
  name: 'ManageClockInLocations',
  components: {
    Card,
    ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElTag, ElCheckbox,
  },
  data() {
    return {
      loaded: false,
      locations: [],
      includeArchived: false,
      showForm: false,
      saving: false,
      editMode: false,
      form: { id: null, name: '', latitude: '', longitude: '', radiusMeters: 100, isArchived: false },
      fetchingLocation: false,
    };
  },
  computed: {
    navigatorSupportsGeolocation() {
      return typeof navigator !== 'undefined' && !!navigator.geolocation;
    },
  },
  created() {
    this.fetchLocations();
  },
  methods: {
    async fetchLocations() {
      this.loaded = false;
      try {
        const res = await api.get('/TimeRegistrationClockInLocation', { params: { includeArchived: this.includeArchived } });
        this.locations = res.data;
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.danger(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      } finally {
        this.loaded = true;
      }
    },
    openCreate() {
      this.editMode = false;
      this.form = { id: null, name: '', latitude: '', longitude: '', radiusMeters: 100, isArchived: false };
      this.showForm = true;
    },
    openEdit(row) {
      this.editMode = true;
      this.form = {
        id: row.id,
        name: row.name,
        latitude: row.latitude,
        longitude: row.longitude,
        radiusMeters: row.radiusMeters,
        isArchived: row.isArchived,
      };
      this.showForm = true;
    },
    async useCurrentLocation() {
      if (!this.navigatorSupportsGeolocation) return;
      this.fetchingLocation = true;
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 })
        );
        this.form.latitude = pos.coords.latitude;
        this.form.longitude = pos.coords.longitude;
      } catch (err) {
        this.$toast.danger(this.$t('timeRegistrationClockInLocations.couldNotGetLocation'), { timeout: 4000 });
      } finally {
        this.fetchingLocation = false;
      }
    },
    validate() {
      if (!this.form.name || !this.form.name.trim()) return this.$t('timeRegistrationClockInLocations.nameRequired');
      const lat = Number(this.form.latitude);
      const lon = Number(this.form.longitude);
      const r = Number(this.form.radiusMeters);
      if (Number.isNaN(lat) || lat < -90 || lat > 90) return this.$t('timeRegistrationClockInLocations.invalidLatitude');
      if (Number.isNaN(lon) || lon < -180 || lon > 180) return this.$t('timeRegistrationClockInLocations.invalidLongitude');
      if (Number.isNaN(r) || r <= 0) return this.$t('timeRegistrationClockInLocations.invalidRadius');
      return null;
    },
    async submitForm() {
      const err = this.validate();
      if (err) { this.$toast.danger(err, { timeout: 3000 }); return; }
      this.saving = true;
      const payload = {
        name: this.form.name.trim(),
        latitude: Number(this.form.latitude),
        longitude: Number(this.form.longitude),
        radiusMeters: Number(this.form.radiusMeters),
      };
      try {
        if (this.editMode) {
          await api.put(`/TimeRegistrationClockInLocation/${this.form.id}`, { ...payload, isArchived: this.form.isArchived });
        } else {
          await api.post('/TimeRegistrationClockInLocation', payload);
        }
        this.$toast.success(this.$t('common.saved'), { timeout: 2000 });
        this.showForm = false;
        await this.fetchLocations();
      } catch (e) {
        this.$toast.danger(e?.response?.data || this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(row) {
      try {
        await ElMessageBox.confirm(
          this.$t('timeRegistrationClockInLocations.deleteConfirm', { name: row.name }),
          this.$t('common.confirm'),
          { type: 'warning' }
        );
      } catch { return; }
      try {
        await api.delete(`/TimeRegistrationClockInLocation/${row.id}`);
        this.$toast.success(this.$t('common.deleted'), { timeout: 2000 });
        await this.fetchLocations();
      } catch (e) {
        this.$toast.danger(e?.response?.data || this.$t('common.unableToDelete'), { timeout: 0 });
      }
    },
  },
};
</script>
